<script lang="ts">
  import { visibleStreams } from '../lib/stores';
  import type { StreamInfo } from '../lib/types';
  import Cell from './Cell.svelte';
  import ArchiveControls from './ArchiveControls.svelte';
  import { startPlayback, stopPlayback, dtToUnix, msToLocal } from '../lib/api';

  // Cross-vendor element fullscreen helpers.
  type AnyEl = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
    msRequestFullscreen?: () => Promise<void>;
  };
  type AnyDoc = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void>;
  };
  const enterFs = (el: AnyEl): Promise<void> | void =>
    (el.requestFullscreen ?? el.webkitRequestFullscreen ?? el.msRequestFullscreen)?.call(el);
  const exitFs = (): Promise<void> | void => {
    const d = document as AnyDoc;
    if ((d.fullscreenElement ?? d.webkitFullscreenElement) === null) return;
    return (document.exitFullscreen ?? d.webkitExitFullscreen)?.call(document);
  };

  type ArchiveState = {
    playbackId: string;
    rangeStartMs: number;
    rangeEndMs: number;
    seekStartMs: number;
    rate: number;
  };

  type Props = {
    fullscreenName: string | null;
    onfullscreenchange: (name: string | null) => void;
    onarchiveopen: (s: StreamInfo) => void;
    onclipopen: (s: StreamInfo) => void;
  };
  let {
    fullscreenName = $bindable(null),
    onfullscreenchange,
    onarchiveopen,
    onclipopen,
  }: Props = $props();

  // Per-stream archive state. Multiple cells can be in archive simultaneously.
  let archives = $state<Record<string, ArchiveState>>({});
  let videoEls = $state<Record<string, HTMLVideoElement>>({});

  export async function startArchive(
    s: StreamInfo,
    startLocal: string,
    endLocal: string,
  ): Promise<void> {
    const r = await startPlayback({
      stream: s.name,
      start: dtToUnix(startLocal),
      end: dtToUnix(endLocal),
    });
    const startMs = new Date(startLocal).getTime();
    const endMs = new Date(endLocal).getTime();
    archives[s.name] = {
      playbackId: r.playback_id,
      rangeStartMs: startMs,
      rangeEndMs: endMs,
      seekStartMs: startMs,
      rate: 1,
    };
  }

  async function backToLive(name: string): Promise<void> {
    const a = archives[name];
    if (!a) return;
    stopPlayback(a.playbackId).catch(() => {});
    delete archives[name];
    archives = { ...archives };
  }

  async function seek(name: string, frac: number): Promise<void> {
    const a = archives[name];
    if (!a) return;
    const newStartMs = a.rangeStartMs + frac * (a.rangeEndMs - a.rangeStartMs);
    const newStartLocal = msToLocal(newStartMs);
    const endLocal = msToLocal(a.rangeEndMs);
    stopPlayback(a.playbackId).catch(() => {});
    try {
      const r = await startPlayback({
        stream: name,
        start: dtToUnix(newStartLocal),
        end: dtToUnix(endLocal),
      });
      archives[name] = { ...a, playbackId: r.playback_id, seekStartMs: newStartMs };
    } catch (_) {
      /* ignore */
    }
  }

  function setRate(name: string, rate: number): void {
    const a = archives[name];
    if (!a) return;
    archives[name] = { ...a, rate };
  }

  // Map of stream name → wrapper DOM ref so we can request native
  // browser fullscreen when a cell is activated.
  const wraps: Record<string, HTMLElement | null> = {};

  $effect(() => {
    if (fullscreenName) {
      const el = wraps[fullscreenName];
      if (el) {
        // Brief delay so the layout has settled to the .active size.
        queueMicrotask(() => {
          try {
            enterFs(el as AnyEl);
          } catch (_) {
            /* ignore — some browsers reject without user gesture, fall
               back to CSS-only fullscreen mode */
          }
        });
      }
    } else {
      try { exitFs(); } catch (_) { /* ignore */ }
    }
  });

  // If the user exits native fullscreen (Esc / swipe), clear our state.
  $effect(() => {
    function onChange(): void {
      const d = document as AnyDoc;
      const inFs = !!(d.fullscreenElement ?? d.webkitFullscreenElement);
      if (!inFs && fullscreenName !== null) {
        fullscreenName = null;
      }
    }
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  });
</script>

<div class="grid" class:fullscreen={fullscreenName !== null}>
  {#each $visibleStreams as s (s.name)}
    {@const a = archives[s.name]}
    <div class="wrap" class:active={fullscreenName === s.name}
         bind:this={wraps[s.name]}>
      <Cell
        stream={s}
        archiveMode={!!a}
        overrideStreamName={a?.playbackId}
        rate={a?.rate ?? 1}
        paused={fullscreenName !== null && fullscreenName !== s.name}
        onarchiveclick={() => onarchiveopen(s)}
        onclipclick={() => onclipopen(s)}
        onfullscreenclick={() => {
          const next = fullscreenName === s.name ? null : s.name;
          fullscreenName = next;
          onfullscreenchange(next);
        }}
        onvideo={(v) => { videoEls[s.name] = v; }}
      />
      {#if a}
        <ArchiveControls
          rangeStartMs={a.rangeStartMs}
          rangeEndMs={a.rangeEndMs}
          seekStartMs={a.seekStartMs}
          video={videoEls[s.name] ?? null}
          rate={a.rate}
          onseek={(f) => seek(s.name, f)}
          onrate={(r) => setRate(s.name, r)}
          onbacktolive={() => backToLive(s.name)}
        />
      {/if}
    </div>
  {/each}
</div>

<style>
  .grid {
    display: grid;
    gap: 8px;
    padding: 12px;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  @media (max-width: 600px) {
    .grid { gap: 6px; padding: 8px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
  }
  .wrap { position: relative; }
  .grid.fullscreen { grid-template-columns: 1fr; gap: 0; padding: 0; }
  .grid.fullscreen .wrap:not(.active) { display: none; }
  .grid.fullscreen .wrap.active { height: calc(100dvh - var(--header-h)); }
  .grid.fullscreen .wrap.active :global(.cell) {
    aspect-ratio: auto;
    height: 100%;
    border-radius: 0;
  }
  /* When the browser puts the wrap into native fullscreen, the wrapper
     fills the whole screen — kill any leftover sizing. */
  .wrap:fullscreen,
  .wrap:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    background: #000;
  }
  .wrap:fullscreen :global(.cell),
  .wrap:-webkit-full-screen :global(.cell) {
    aspect-ratio: auto;
    height: 100%;
    width: 100%;
    border: 0;
    border-radius: 0;
  }
  .wrap:fullscreen :global(video),
  .wrap:-webkit-full-screen :global(video) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
</style>
